import type { SavedSearchItem } from '@iconik-io/iconik-api/src/schema_patched/saved_search';
import type { AssetsCustomActionSchemaWithId } from '@iconik-io/iconik-api/src/services/assets';
import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
  type ObjectType,
} from '@iconik-io/iconik-api/src/types/object_types';
import type {
  Permission,
  SearchItemEntity,
} from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import type { UserRoles } from '@iconik-io/shared-lib/src/utils/roles';
import { customActionCallback } from '@iconik-io/web/src/ducks/custom_actions/thunks';

import { APPROVE_REJECT_BULK_MODAL } from '../../constants/modals';
import {
  ASSET_PAGE_ROUTE,
  ASSET_VERSION_PAGE_ROUTE,
  COLLECTION_PAGE_ROUTE,
} from '../../constants/routes';
import { openModal } from '../../ducks/modals_manager/slice';
import { selectIsLatestVersion } from '../../ducks/version/selectors';
import { ICONS } from '../../icons/icon';
import type { AppDispatch, AppState } from '../../store/types';

export function onCustomAction(
  dispatch: AppDispatch,
  items: SearchItemEntity[],
  customAction: AssetsCustomActionSchemaWithId,
  fromCollectionId?: string,
): void {
  const collections: string[] = [];
  const assets: string[] = [];
  items.forEach((i) => {
    if (i.object_type === 'collections') {
      collections.push(i.id);
    } else {
      assets.push(i.id);
    }
  });
  const data = {
    collection_ids: collections,
    asset_ids: assets,
    from_collection_id: fromCollectionId,
  };
  dispatch(
    customActionCallback(
      {
        label: customAction.title,
        id: customAction.id,
        context: customAction.context,
        type: customAction.type,
        metadataView: customAction.metadata_view,
      },
      data,
    ),
  );
}

export function getCanRemoveFromCollection(
  state: AppState,
  userRoles: UserRoles,
  contextItems: SearchItemEntity[],
) {
  const isCollectionPage = state.routeParams.route === COLLECTION_PAGE_ROUTE;
  if (userRoles.canWriteCollections && isCollectionPage) {
    const currentCollectionId = state.routeParams.params.id;
    // Check if context items really belong to the current collection,
    // they can't in case when e.g. the context item is in the collections tree.
    const everyItemBelongsToCurrentCollection = contextItems.every((item) => {
      const inCollections = item.in_collections ?? [];
      if (
        item.object_type === OBJECT_TYPE_ASSETS &&
        inCollections.includes(currentCollectionId)
      ) {
        return true;
      }
      if (
        item.object_type === OBJECT_TYPE_COLLECTIONS &&
        item.parent_id === currentCollectionId
      ) {
        return true;
      }
      return false;
    });

    const hasCollections = contextItems.some(
      (item) => item.object_type === 'collections',
    );
    if (hasCollections) {
      return false;
    }

    // Additionally check the write permission for the current collection.
    const currentCollectionAcls =
      state.acls.collections.entityCurrentUserAcls[currentCollectionId];

    const existsInOtherCollection = contextItems.every(({ in_collections }) => {
      const inCollections = in_collections ?? [];
      return (
        inCollections.includes(currentCollectionId) && inCollections.length > 1
      );
    });
    const webCanTopLevelUpload =
      userRoles.webCanTopLevelUpload || existsInOtherCollection;
    if (
      everyItemBelongsToCurrentCollection &&
      currentCollectionAcls?.write &&
      webCanTopLevelUpload
    ) {
      return true;
    }
  }
  return false;
}

export type ContextItemsAttributes = {
  permissions: Permission[];
  isAdmin: boolean;
  isOnlyAssets: boolean;
  isOnlyCollections: boolean;
  isLatestVersion: boolean;
  selected: number;
  canDeleteAsset: boolean;
  canDeleteCollection: boolean;
  canShareEntity: boolean;
  canWrite: boolean;
  canChangeEntityAcl: boolean;
  hasLinkAssetType: boolean;
  hasPlaceholderAssetType: boolean;
  hasSubclipAssetType: boolean;
  hasPostAssetType: boolean;
  // Used for custom actions to indicate from within which collection the action was triggered from
  fromCollectionId?: string;
};

export function getContextItemsAttributes(
  state: AppState,
  contextItems: SearchItemEntity[],
  fromCollectionId?: string,
): ContextItemsAttributes {
  const { userRoles } = state.rolesReducer;
  const [contextItem] = contextItems;
  const permissions: Permission[] = (contextItem?.permissions ??
    []) as Permission[];
  const isAdmin = Boolean(state.authReducer.userData.is_admin);
  const selected = contextItems.length;
  const canDeleteAsset = !contextItem?.is_blocked
    ? (userRoles.canDeleteAssets && permissions.includes('delete')) || isAdmin
    : false;

  const canDeleteCollection =
    (userRoles.canDeleteCollections && permissions.includes('delete')) ||
    isAdmin;
  const canShareEntity =
    (userRoles.canWriteShares && permissions.includes('read')) || isAdmin;
  const canWrite = permissions.includes('write') || isAdmin;
  const canChangeEntityAcl =
    (permissions.includes('change-acl') && userRoles.canWriteAcls) || isAdmin;
  const isOnlyAssets = !contextItems.some(
    (item) => item.object_type === 'collections',
  );
  const isOnlyCollections = !contextItems.some(
    (item) => item.object_type === 'assets',
  );

  const hasLinkAssetType = contextItems.some((item) => item.type === 'LINK');

  const hasPlaceholderAssetType = contextItems.some(
    (item) => item.type === 'PLACEHOLDER',
  );

  const hasSubclipAssetType = contextItems.some(
    (item) => item.type === 'SUBCLIP',
  );

  const hasPostAssetType = contextItems.some((item) => item.type === 'POST');

  // Some actions are allowed only when viewing the latest version on the asset page
  const isLatestVersion = [ASSET_PAGE_ROUTE, ASSET_VERSION_PAGE_ROUTE].includes(
    state.routeParams.route,
  )
    ? selectIsLatestVersion(
        state,
        state.routeParams.params.id,
        state.routeParams.params.versionId,
      )
    : true;

  return {
    permissions,
    isAdmin,
    isOnlyAssets,
    isOnlyCollections,
    isLatestVersion,
    selected,
    canDeleteAsset,
    canDeleteCollection,
    canShareEntity,
    canWrite,
    canChangeEntityAcl,
    hasLinkAssetType,
    hasPlaceholderAssetType,
    hasSubclipAssetType,
    hasPostAssetType,
    fromCollectionId,
  };
}

export const emptyContextMenuList = [
  {
    label: 'No actions available',
    disabled: true,
    icon: ICONS.Info,
  },
];

export const reviewBulkMenu = (
  contextItems: (SearchItemEntity | SavedSearchItem)[],
  objectType: ObjectType,
  actionType: string,
  multiple?: boolean,
) => {
  const assets = multiple
    ? contextItems.filter(
        ({ object_type }) => object_type === OBJECT_TYPE_ASSETS,
      )
    : contextItems;

  const actionIcons = (action: string) => {
    if (action === 'Approve') {
      return ICONS.ThumbUp;
    }
    if (action === 'Reject') {
      return ICONS.ThumbDown;
    }
    return ICONS.Reset;
  };

  const label =
    objectType === OBJECT_TYPE_ASSETS && !multiple
      ? `${actionType}...`
      : `${actionType} Assets...`;

  return {
    label,
    amount: multiple ? assets.length : undefined,
    action: () => (dispatch: AppDispatch) => {
      dispatch(
        openModal({
          id: APPROVE_REJECT_BULK_MODAL,
          contextItems,
          options: { action: actionType, objectType },
        }),
      );
    },
    icon: actionIcons(actionType),
    multiple,
  };
};
